import * as React from "react";
import { useMemo } from "react";
import PropTypes from "prop-types";
import isString from "lodash-es/isString";
import classNames from "classnames";

import RightScreen from "./RightScreen";
import LeftScreen from "./LeftScreen";
import VectorLogo from "./VectorSvg";

import { extractSvgPath } from "utils/extractSvgPath";

import { DARK } from "constants/appModes";

const AppThemePreview = ({
  logo,
  mode,
  gradient = [],
  primary = "",
  name = "",
  logoStyle,
  className,
}) => {
  const [svgPath, setSvgPath] = React.useState(null);
  const isDark = useMemo(() => mode === DARK, [mode]);
  const isFile = logo instanceof File;
  const previewSvgClassName = classNames("app-theme-preview-svg", className);

  React.useEffect(() => {
    if (!isFile) return;

    if (logo.type === "image/svg+xml") {
      extractSvgPath(logo).then((path) => setSvgPath(path || null));
    } else {
      setSvgPath(null);
    }

    return () => setSvgPath(null);
  }, [logo]);

  const vectorPath = React.useMemo(() => {
    if (svgPath) return svgPath;

    if (isString(logo)) {
      const trimmed = logo.trim();
      if (
        trimmed.startsWith("M") ||
        trimmed.startsWith("m") ||
        trimmed.startsWith("<svg")
      ) {
        return trimmed;
      }
    }

    return null;
  }, [logo, svgPath]);

  const renderLogo = () => {
    if (!logo) return null;

    if (vectorPath) {
      return <VectorLogo path={vectorPath} style={logoStyle} />;
    }

    if (isFile) {
      return (
        <img
          src={URL.createObjectURL(logo)}
          alt="App logo preview"
          style={logoStyle}
        />
      );
    }

    return null;
  };

  return (
    <div className="app-theme-preview">
      <div className={previewSvgClassName}>
        {renderLogo()}
        <LeftScreen gradient={gradient} name={name} />
      </div>
      <div className={previewSvgClassName}>
        {renderLogo()}
        <RightScreen
          gradient={gradient}
          primary={primary}
          name={name}
          fillSurface={isDark ? "#191C1E" : "#F0F0F3"}
          fillOnSurface={isDark ? "#44474E" : "#E0E2EC"}
          bottomBarFill={isDark ? "#34383D" : "#EFF5FC"}
        />
      </div>
    </div>
  );
};

AppThemePreview.propTypes = {
  logo: PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.string]),
  gradient: PropTypes.arrayOf(PropTypes.string),
  primary: PropTypes.string,
  mode: PropTypes.string,
  name: PropTypes.string,
  logoStyle: PropTypes.object,
  className: PropTypes.string,
};

export default AppThemePreview;
